import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from 'xterm';
import { WebglAddon } from 'xterm-addon-webgl';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { FitAddon } from 'xterm-addon-fit';
import { RefreshCw, Terminal as TerminalIcon, Cpu, Send, Circle, Plus, X, ToggleLeft, ToggleRight, Bell } from 'lucide-react';
import 'xterm/css/xterm.css';

// Notification helper
const showNotification = (title: string, body: string, onClick?: () => void) => {
  // Play notification sound
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU');
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch {}

  // Browser notification if permitted
  if ('Notification' in window && Notification.permission === 'granted') {
    const notif = new Notification(title, { body, icon: '/icons/terminal.png', tag: 'terminal-complete' });
    if (onClick) notif.onclick = onClick;
    setTimeout(() => notif.close(), 5000);
  }
};

// Request notification permission on load
if ('Notification' in window && Notification.permission === 'default') {
  Notification.requestPermission();
}

const getWsUrl = (terminalId: string) => {
  const base = window.location.protocol === 'https:'
    ? `wss://${window.location.host}/ws/claude-terminal`
    : 'ws://localhost:3102';
  return `${base}?terminal=${encodeURIComponent(terminalId)}`;
};

const STORAGE_KEY = 'terminal-tabs-v3';
const MPC_MODE_KEY = 'terminal-mpc-mode';

// Terminal theme - Catppuccin Mocha vibes
const termTheme = {
  background: '#0a0a12',
  foreground: '#e5e7eb',
  cursor: '#06b6d4',
  cursorAccent: '#0a0a12',
  selectionBackground: 'rgba(6, 182, 212, 0.3)',
  black: '#1e1e2e',
  red: '#f38ba8',
  green: '#a6e3a1',
  yellow: '#f9e2af',
  blue: '#89b4fa',
  magenta: '#cba6f7',
  cyan: '#94e2d5',
  white: '#bac2de'
};

// MPC Dispatch Modes
type MPCMode = 'first-available' | 'back-and-forth';

interface TerminalSlot {
  id: string;
  name: string;
}

// Default slots (used on first load)
const DEFAULT_TERMINAL_SLOTS: TerminalSlot[] = [
  { id: 'terminal-1', name: 'Terminal 1' },
  { id: 'terminal-2', name: 'Terminal 2' },
  { id: 'terminal-3', name: 'Terminal 3' },
  { id: 'terminal-4', name: 'Terminal 4' },
];

// Generate unique ID for new terminals
let terminalIdCounter = 5; // Start after default 4
const generateTerminalId = (): string => {
  const id = `terminal-${terminalIdCounter}`;
  terminalIdCounter++;
  return id;
};

// Load slots from storage or use defaults
const loadTerminalSlots = (): TerminalSlot[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      if (data.slots && Array.isArray(data.slots) && data.slots.length > 0) {
        // Update counter to avoid ID collisions
        const maxId = Math.max(...data.slots.map((s: TerminalSlot) => {
          const match = s.id.match(/terminal-(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        }));
        terminalIdCounter = Math.max(terminalIdCounter, maxId + 1);
        return data.slots;
      }
    }
  } catch {}
  return [...DEFAULT_TERMINAL_SLOTS];
};

// Load MPC mode from storage
const loadMPCMode = (): MPCMode => {
  try {
    const saved = localStorage.getItem(MPC_MODE_KEY);
    if (saved === 'back-and-forth') return 'back-and-forth';
  } catch {}
  return 'first-available';
};

interface TerminalTab {
  id: string;
  name: string;
  terminal: Terminal | null;
  ws: WebSocket | null;
  fitAddon: FitAddon | null;
  status: 'disconnected' | 'connecting' | 'connected';
  busy: boolean;
  lastActivity: number;
  dataHandler: { dispose: () => void } | null;
  completedAt?: number; // Timestamp when task completed
}

// Toast notification state
interface ToastNotification {
  id: string;
  message: string;
  type: 'success' | 'info' | 'warning';
  timestamp: number;
}

// Load active tab from storage
const loadActiveTab = (): string => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      return data.activeId || 'terminal-1';
    }
  } catch {}
  return 'terminal-1';
};

// Save tabs state (slots and active tab)
const saveTabsState = (slots: TerminalSlot[], activeId: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ slots, activeId }));
  } catch {}
};

// Save MPC mode
const saveMPCMode = (mode: MPCMode) => {
  try {
    localStorage.setItem(MPC_MODE_KEY, mode);
  } catch {}
};

const TerminalPage: React.FC = () => {
  const [terminalSlots, setTerminalSlots] = useState<TerminalSlot[]>(loadTerminalSlots);
  const [tabs, setTabs] = useState<TerminalTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>(loadActiveTab());
  const [mountTrigger, setMountTrigger] = useState(0);
  const [pmCommand, setPmCommand] = useState('');
  const [mpcMode, setMpcMode] = useState<MPCMode>(loadMPCMode);
  const [backAndForthIndex, setBackAndForthIndex] = useState(0);
  const [backAndForthDirection, setBackAndForthDirection] = useState<1 | -1>(1); // 1 = forward, -1 = backward
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<TerminalTab[]>([]);
  const initializedRef = useRef(false);

  // Add toast notification
  const addToast = useCallback((message: string, type: ToastNotification['type'] = 'success') => {
    const id = `toast-${Date.now()}`;
    setToasts(prev => [...prev, { id, message, type, timestamp: Date.now() }]);
    // Auto-remove after 5s
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  // Notify when shell completes
  const notifyShellComplete = useCallback((tabName: string, tabId: string) => {
    if (!notificationsEnabled) return;

    const message = `${tabName} completed`;
    addToast(message, 'success');

    // Browser notification if not focused
    if (document.hidden) {
      showNotification('Shell Complete', message, () => {
        window.focus();
        setActiveTabId(tabId);
      });
    }
  }, [notificationsEnabled, addToast]);

  // Keep ref in sync with state
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  // Save state when tabs or active tab changes
  useEffect(() => {
    if (initializedRef.current) {
      saveTabsState(terminalSlots, activeTabId);
    }
  }, [activeTabId, terminalSlots]);

  // Save MPC mode when it changes
  useEffect(() => {
    saveMPCMode(mpcMode);
  }, [mpcMode]);

  // Initialize terminal tabs from slots
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const initialTabs: TerminalTab[] = terminalSlots.map(slot => ({
      id: slot.id,
      name: slot.name,
      terminal: null,
      ws: null,
      fitAddon: null,
      status: 'disconnected',
      busy: false,
      lastActivity: 0,
      dataHandler: null
    }));

    setTabs(initialTabs);

    // Validate active tab exists (project-manager is a valid special tab)
    if (activeTabId !== 'project-manager' && !terminalSlots.find(s => s.id === activeTabId) && terminalSlots.length > 0) {
      setActiveTabId(terminalSlots[0].id);
    }
  }, [activeTabId, terminalSlots]);

  // Add a new terminal tab
  const addTerminal = useCallback(() => {
    const newId = generateTerminalId();
    const newSlot: TerminalSlot = {
      id: newId,
      name: `Terminal ${newId.split('-')[1]}`
    };

    setTerminalSlots(prev => [...prev, newSlot]);

    setTabs(prev => [...prev, {
      id: newId,
      name: newSlot.name,
      terminal: null,
      ws: null,
      fitAddon: null,
      status: 'disconnected',
      busy: false,
      lastActivity: 0,
      dataHandler: null
    }]);

    setActiveTabId(newId);
  }, []);

  // Remove a terminal tab
  const removeTerminal = useCallback((tabId: string) => {
    // Must keep at least one terminal
    if (terminalSlots.length <= 1) return;

    // Find the tab to clean up
    const tabToRemove = tabsRef.current.find(t => t.id === tabId);
    if (tabToRemove) {
      tabToRemove.dataHandler?.dispose();
      tabToRemove.ws?.close();
      tabToRemove.terminal?.dispose();
    }

    setTerminalSlots(prev => prev.filter(s => s.id !== tabId));
    setTabs(prev => prev.filter(t => t.id !== tabId));

    // If removing active tab, select nearest neighbor
    if (activeTabId === tabId) {
      const currentIndex = terminalSlots.findIndex(s => s.id === tabId);
      const newIndex = currentIndex > 0 ? currentIndex - 1 : 1;
      const newActiveSlot = terminalSlots[newIndex] || terminalSlots[0];
      if (newActiveSlot && newActiveSlot.id !== tabId) {
        setActiveTabId(newActiveSlot.id);
      } else if (terminalSlots.length > 1) {
        // Find any slot that isn't the one being removed
        const fallback = terminalSlots.find(s => s.id !== tabId);
        if (fallback) setActiveTabId(fallback.id);
      }
    }
  }, [activeTabId, terminalSlots]);

  // Initialize terminal for a tab
  const initTerminal = useCallback((tabId: string) => {
    const container = containerRef.current;
    if (!container) return;

    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId || tab.terminal) return tab;

      const isMobile = window.innerWidth < 640;
      const terminal = new Terminal({
        cols: isMobile ? 80 : 120,
        rows: isMobile ? 24 : 32,
        allowProposedApi: true,
        theme: termTheme,
        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
        fontSize: isMobile ? 12 : 14,
        cursorBlink: true,
        cursorStyle: 'block',
        scrollback: 5000
      });

      const fitAddon = new FitAddon();
      terminal.loadAddon(fitAddon);

      return { ...tab, terminal, fitAddon, status: 'connecting', dataHandler: null };
    }));
    setTimeout(() => setMountTrigger(t => t + 1), 50);
  }, []);

  // Connect WebSocket for a tab
  const connectTab = useCallback((tabId: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId || !tab.terminal) return tab;
      if (tab.ws && (tab.ws.readyState === WebSocket.OPEN || tab.ws.readyState === WebSocket.CONNECTING)) return tab;

      tab.dataHandler?.dispose();

      const ws = new WebSocket(getWsUrl(tabId));
      const terminal = tab.terminal;

      ws.onopen = () => {
        console.log(`[${tabId}] WebSocket connected`);
        setTabs(p => p.map(t => t.id === tabId ? { ...t, status: 'connected' } : t));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          switch (msg.type) {
            case 'output':
              terminal?.write(msg.data);
              // Mark as busy when receiving output, track activity
              setTabs(p => p.map(t => t.id === tabId ? { ...t, busy: true, lastActivity: Date.now() } : t));
              // Auto-mark as not busy after 2s of no output and notify
              setTimeout(() => {
                setTabs(p => p.map(t => {
                  if (t.id === tabId && Date.now() - t.lastActivity > 1500 && t.busy) {
                    // Shell completed - notify
                    notifyShellComplete(t.name, tabId);
                    return { ...t, busy: false, completedAt: Date.now() };
                  }
                  return t;
                }));
              }, 2000);
              break;
            case 'connected':
              const statusMsg = msg.resumed
                ? `\x1b[36m↻ Reconnected to session\x1b[0m`
                : `\x1b[32m✓ New terminal session\x1b[0m`;
              terminal?.writeln(`\r\n${statusMsg}\r\n`);
              break;
            case 'exit':
              terminal?.writeln(`\r\n\x1b[33mSession ended (code: ${msg.code})\x1b[0m`);
              setTabs(p => p.map(t => t.id === tabId ? { ...t, status: 'disconnected', busy: false } : t));
              break;
            case 'error':
              terminal?.writeln(`\r\n\x1b[31mError: ${msg.message}\x1b[0m`);
              break;
          }
        } catch {
          terminal?.write(event.data);
        }
      };

      ws.onclose = () => {
        console.log(`[${tabId}] WebSocket closed`);
        setTabs(p => p.map(t => t.id === tabId ? { ...t, status: 'disconnected', busy: false } : t));
      };

      ws.onerror = (error) => {
        console.error(`[${tabId}] WebSocket error:`, error);
        setTabs(p => p.map(t => t.id === tabId ? { ...t, status: 'disconnected', busy: false } : t));
      };

      const dataHandler = terminal.onData((data) => {
        if (data.includes('\x1b[?') || data.includes('\x1b[>')) return;
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'input', data }));
        }
      });

      return { ...tab, ws, dataHandler, status: 'connecting' };
    }));
  }, []);

  // Reconnect a tab
  const reconnectTab = useCallback((tabId: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;

      tab.dataHandler?.dispose();
      tab.ws?.close();
      tab.terminal?.clear();
      tab.terminal?.writeln('\x1b[33mReconnecting...\x1b[0m');

      return { ...tab, ws: null, dataHandler: null, status: 'connecting', busy: false };
    }));

    setTimeout(() => connectTab(tabId), 500);
  }, [connectTab]);

  // Send command to a specific terminal
  const sendToTerminal = useCallback((tabId: string, command: string) => {
    const tab = tabsRef.current.find(t => t.id === tabId);
    if (tab?.ws?.readyState === WebSocket.OPEN) {
      tab.ws.send(JSON.stringify({ type: 'input', data: command + '\n' }));
      setTabs(p => p.map(t => t.id === tabId ? { ...t, busy: true, lastActivity: Date.now() } : t));
    }
  }, []);

  // Get next terminal for back-and-forth mode
  const getBackAndForthTerminal = useCallback((): TerminalTab | undefined => {
    const connectedTabs = tabsRef.current.filter(t => t.status === 'connected' && !t.busy);
    if (connectedTabs.length === 0) return undefined;
    if (connectedTabs.length === 1) return connectedTabs[0];

    // Use index within connected tabs
    let idx = backAndForthIndex;
    let dir = backAndForthDirection;

    // Clamp index to valid range
    if (idx >= connectedTabs.length) {
      idx = connectedTabs.length - 1;
      dir = -1;
    }
    if (idx < 0) {
      idx = 0;
      dir = 1;
    }

    const selected = connectedTabs[idx];

    // Calculate next index
    let nextIdx = idx + dir;
    let nextDir = dir;

    // Handle bounce at edges
    if (nextIdx >= connectedTabs.length) {
      nextIdx = connectedTabs.length - 2;
      nextDir = -1;
    } else if (nextIdx < 0) {
      nextIdx = 1;
      nextDir = 1;
    }

    // Handle edge case: only 2 terminals (N=2: alternates 0,1,0,1...)
    if (connectedTabs.length === 2) {
      nextIdx = idx === 0 ? 1 : 0;
      nextDir = dir; // Direction doesn't matter for N=2
    }

    setBackAndForthIndex(Math.max(0, Math.min(nextIdx, connectedTabs.length - 1)));
    setBackAndForthDirection(nextDir);

    return selected;
  }, [backAndForthIndex, backAndForthDirection]);

  // Project Manager: dispatch to available terminal
  const dispatchCommand = useCallback((command: string) => {
    if (!command.trim()) return;

    let target: TerminalTab | undefined;

    if (mpcMode === 'first-available') {
      // Mode 1: Find first available (connected and not busy) terminal
      target = tabsRef.current.find(t => t.status === 'connected' && !t.busy);
    } else {
      // Mode 2: Back-and-forth traversal
      target = getBackAndForthTerminal();
    }

    if (target) {
      sendToTerminal(target.id, command);
      setPmCommand('');
      console.log(`[PM] Dispatched to ${target.name} (${mpcMode}): ${command}`);
    } else {
      console.warn('[PM] No available terminals');
      alert('All terminals are busy or disconnected. Please wait or connect a terminal.');
    }
  }, [sendToTerminal, mpcMode, getBackAndForthTerminal]);

  // Toggle MPC mode
  const toggleMPCMode = useCallback(() => {
    setMpcMode(prev => prev === 'first-available' ? 'back-and-forth' : 'first-available');
    // Reset back-and-forth state when switching modes
    setBackAndForthIndex(0);
    setBackAndForthDirection(1);
  }, []);

  // Mount/unmount terminal when switching tabs
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !activeTabId || activeTabId === 'project-manager') return;

    const tab = tabsRef.current.find(t => t.id === activeTabId);
    if (!tab) return;

    container.innerHTML = '';

    if (!tab.terminal) {
      initTerminal(activeTabId);
      return;
    }

    if (!tab.terminal.element?.parentElement) {
      tab.terminal.open(container);

      try {
        const webgl = new WebglAddon();
        tab.terminal.loadAddon(webgl);
      } catch (e) {
        console.warn('WebGL addon failed, using canvas fallback');
      }

      try {
        tab.terminal.loadAddon(new WebLinksAddon());
      } catch (e) {
        console.warn('WebLinks addon failed');
      }
    } else {
      container.appendChild(tab.terminal.element);
    }

    if (tab.fitAddon) {
      // Delay fit to ensure container is fully laid out, then refresh
      requestAnimationFrame(() => {
        setTimeout(() => {
          try {
            tab.fitAddon?.fit();
            tab.terminal?.refresh(0, tab.terminal.rows - 1);
          } catch (e) {
            console.warn('[Terminal] Tab switch fit error:', e);
          }
        }, 50);
      });
    }

    if (!tab.ws || tab.ws.readyState !== WebSocket.OPEN) {
      connectTab(activeTabId);
    }

    tab.terminal.focus();

  }, [activeTabId, initTerminal, connectTab, mountTrigger]);

  // Handle resize with debounce and proper refresh
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const doResize = () => {
      if (activeTabId === 'project-manager') return;
      const tab = tabsRef.current.find(t => t.id === activeTabId);
      if (!tab?.fitAddon || !tab?.terminal) return;

      requestAnimationFrame(() => {
        try {
          tab.fitAddon!.fit();
          // Force refresh to sync canvas with new dimensions
          tab.terminal!.refresh(0, tab.terminal!.rows - 1);

          if (tab.ws?.readyState === WebSocket.OPEN && tab.terminal!.cols >= 20 && tab.terminal!.rows >= 5) {
            tab.ws.send(JSON.stringify({
              type: 'resize',
              cols: tab.terminal!.cols,
              rows: tab.terminal!.rows
            }));
          }
        } catch (e) {
          console.warn('[Terminal] Resize error:', e);
        }
      });
    };

    const debouncedResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(doResize, 50);
    };

    window.addEventListener('resize', debouncedResize);

    // Also observe container size changes
    const container = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    if (container) {
      resizeObserver = new ResizeObserver(debouncedResize);
      resizeObserver.observe(container);
    }

    // Initial fit after mount
    const initTimer = setTimeout(doResize, 100);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      resizeObserver?.disconnect();
      if (resizeTimeout) clearTimeout(resizeTimeout);
      clearTimeout(initTimer);
    };
  }, [activeTabId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+1-9 - Switch to terminal by position (if exists)
      if (e.ctrlKey && /^[1-9]$/.test(e.key)) {
        const index = parseInt(e.key, 10) - 1;
        if (index < terminalSlots.length) {
          e.preventDefault();
          setActiveTabId(terminalSlots[index].id);
        }
      }
      // Ctrl+0 - Project Manager
      if (e.ctrlKey && e.key === '0') {
        e.preventDefault();
        setActiveTabId('project-manager');
      }
      // Ctrl+N - Add new terminal
      if (e.ctrlKey && e.key === 'n' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        addTerminal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [terminalSlots, addTerminal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      tabsRef.current.forEach(tab => {
        tab.dataHandler?.dispose();
        tab.ws?.close();
        tab.terminal?.dispose();
      });
    };
  }, []);

  const activeTab = tabs.find(t => t.id === activeTabId);

  const statusColors = {
    disconnected: 'bg-red-500/20 text-red-400 border-red-500/30',
    connecting: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    connected: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  const statusDot = {
    disconnected: 'bg-red-500',
    connecting: 'bg-yellow-500 animate-pulse',
    connected: 'bg-green-500'
  };

  const statusText = {
    disconnected: 'not working',
    connecting: 'checking',
    connected: 'working'
  };

  const statusTextColor = {
    disconnected: 'text-red-400',
    connecting: 'text-yellow-400',
    connected: 'text-green-400'
  };

  const isProjectManager = activeTabId === 'project-manager';

  return (
    <main className="flex-1 overflow-hidden p-2 sm:p-4 bg-[#0a0a0a]">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-white shrink-0">
              {isProjectManager ? 'Project Manager' : 'Terminal'}
            </h1>
            {activeTab && !isProjectManager && (
              <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium border ${statusColors[activeTab.status]}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${statusDot[activeTab.status]}`}></span>
                <span className="hidden sm:inline">{activeTab.status}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Notification toggle */}
            <button
              onClick={() => setNotificationsEnabled(prev => !prev)}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors border ${
                notificationsEnabled
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
              }`}
              title={notificationsEnabled ? 'Notifications ON' : 'Notifications OFF'}
            >
              <Bell className={`w-3.5 h-3.5 ${notificationsEnabled ? '' : 'opacity-50'}`} />
            </button>
            {activeTab && !isProjectManager && (
              <button
                onClick={() => reconnectTab(activeTab.id)}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-lg text-xs bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors border border-cyan-500/30"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Reconnect</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-2 bg-[#0d0d16] rounded-lg p-1 border border-gray-800/50 overflow-x-auto">
          {tabs.map(tab => (
            <div
              key={tab.id}
              className={`
                group flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md cursor-pointer transition-all shrink-0
                ${tab.id === activeTabId
                  ? 'bg-gradient-to-b from-cyan-500/20 to-cyan-600/10 text-cyan-300 border border-cyan-500/30'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 border border-transparent'}
              `}
            >
              <div className="flex items-center gap-1 sm:gap-2" onClick={() => setActiveTabId(tab.id)}>
                <TerminalIcon className="w-3.5 h-3.5" />
                <span className="text-xs sm:text-sm font-medium">{tab.name}</span>
                <span className="text-gray-500">-</span>
                <span className={`text-[10px] sm:text-xs ${statusTextColor[tab.status]}`}>{statusText[tab.status]}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${statusDot[tab.status]}`}></span>
                {tab.busy && (
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" title="Busy"></span>
                )}
              </div>
              {/* Remove button - only show if more than 1 tab */}
              {tabs.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeTerminal(tab.id); }}
                  className="ml-1 p-0.5 rounded hover:bg-red-500/30 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                  title="Close terminal"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {/* Add Terminal Button */}
          <button
            onClick={addTerminal}
            className="flex items-center gap-1 px-2 py-1.5 rounded-md text-gray-500 hover:text-green-400 hover:bg-green-500/20 transition-all shrink-0 border border-transparent hover:border-green-500/30"
            title="Add terminal (Ctrl+N)"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          {/* Project Manager Tab */}
          <div
            onClick={() => setActiveTabId('project-manager')}
            className={`
              group flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md cursor-pointer transition-all shrink-0
              ${activeTabId === 'project-manager'
                ? 'bg-gradient-to-b from-purple-500/20 to-purple-600/10 text-purple-300 border border-purple-500/30'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 border border-transparent'}
            `}
          >
            <Cpu className="w-3.5 h-3.5" />
            <span className="text-xs sm:text-sm font-medium">PM</span>
          </div>
        </div>

        {/* Terminal container or Project Manager */}
        {isProjectManager ? (
          <div className="flex-1 min-h-0 bg-[#0a0a12] rounded-xl overflow-hidden border border-gray-800/50 shadow-2xl p-4 flex flex-col">
            {/* Terminal Status Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {tabs.map(tab => (
                <div
                  key={tab.id}
                  className={`p-3 rounded-lg border ${
                    tab.status === 'connected'
                      ? tab.busy
                        ? 'bg-yellow-500/10 border-yellow-500/30'
                        : 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{tab.name}</span>
                    <div className="flex items-center gap-2">
                      {tab.busy && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">BUSY</span>
                      )}
                      <Circle
                        className={`w-2.5 h-2.5 ${
                          tab.status === 'connected'
                            ? 'text-green-400 fill-green-400'
                            : tab.status === 'connecting'
                            ? 'text-yellow-400 fill-yellow-400 animate-pulse'
                            : 'text-red-400 fill-red-400'
                        }`}
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {tab.status === 'connected'
                      ? tab.busy ? 'Processing...' : 'Ready'
                      : tab.status === 'connecting' ? 'Connecting...' : 'Disconnected'}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => setActiveTabId(tab.id)}
                      className="text-[10px] px-2 py-1 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
                    >
                      View
                    </button>
                    {tab.status !== 'connected' && (
                      <button
                        onClick={() => reconnectTab(tab.id)}
                        className="text-[10px] px-2 py-1 rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* MPC Mode Toggle */}
            <div className="mb-4 p-3 rounded-lg bg-[#0d0d16] border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium text-white mb-0.5">Dispatch Mode</div>
                  <div className="text-[10px] text-gray-500">
                    {mpcMode === 'first-available'
                      ? 'Mode 1: First available terminal'
                      : 'Mode 2: Back-and-forth traversal'}
                  </div>
                </div>
                <button
                  onClick={toggleMPCMode}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all ${
                    mpcMode === 'back-and-forth'
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'bg-gray-700/50 text-gray-400 border border-gray-600'
                  }`}
                >
                  {mpcMode === 'back-and-forth' ? (
                    <ToggleRight className="w-4 h-4" />
                  ) : (
                    <ToggleLeft className="w-4 h-4" />
                  )}
                  {mpcMode === 'first-available' ? 'Mode 1' : 'Mode 2'}
                </button>
              </div>
            </div>

            {/* Command Input */}
            <div className="mt-auto">
              <div className="text-xs text-gray-500 mb-2">
                {mpcMode === 'first-available'
                  ? 'Dispatch command to first available terminal:'
                  : 'Dispatch command using back-and-forth:'}
              </div>
              <form
                onSubmit={(e) => { e.preventDefault(); dispatchCommand(pmCommand); }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={pmCommand}
                  onChange={(e) => setPmCommand(e.target.value)}
                  placeholder="Enter command to dispatch..."
                  className="flex-1 bg-[#0d0d16] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors border border-purple-500/30"
                >
                  <Send className="w-4 h-4" />
                  Dispatch
                </button>
              </form>
              <div className="text-[10px] text-gray-600 mt-2">
                Available: {tabs.filter(t => t.status === 'connected' && !t.busy).length}/{tabs.length} terminals
                {mpcMode === 'back-and-forth' && (
                  <span className="ml-2 text-cyan-500">• Next: index {backAndForthIndex}</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 bg-[#0a0a12] rounded-xl overflow-hidden border border-gray-800/50 shadow-2xl">
            <div ref={containerRef} className="h-full p-1 sm:p-3" />
          </div>
        )}

        {/* Keyboard shortcuts hint */}
        <div className="mt-2 text-xs text-gray-600 hidden sm:flex items-center gap-4 flex-wrap">
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 font-mono">Ctrl+1-{Math.min(9, tabs.length)}</kbd> terminals
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 font-mono">Ctrl+0</kbd> project manager
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 font-mono">Ctrl+N</kbd> new terminal
          </span>
        </div>
      </div>

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-sm
                animate-in slide-in-from-right-5 duration-300
                ${toast.type === 'success'
                  ? 'bg-green-500/20 border-green-500/40 text-green-300'
                  : toast.type === 'warning'
                  ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-300'
                  : 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'}
              `}
            >
              <div className={`w-2 h-2 rounded-full ${
                toast.type === 'success' ? 'bg-green-400' :
                toast.type === 'warning' ? 'bg-yellow-400' : 'bg-cyan-400'
              }`} />
              <span className="text-sm font-medium">{toast.message}</span>
              <button
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                className="ml-auto text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default TerminalPage;
